import { useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Megaphone, 
  TrendingUp, 
  Settings, 
  LogOut,
  BarChart3,
  X,
  Users,
  Sparkles,
  FlaskConical,
  Radar,
  ArrowUpRight
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Sidebar = ({ user, isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/login");
  };

  // Global admin email
  const GLOBAL_ADMIN_EMAIL = "matgeo.2008@gmail.com";
  const isAdmin = user?.email === GLOBAL_ADMIN_EMAIL;
  const isSimulatorUser = user?.email === GLOBAL_ADMIN_EMAIL || user?.email === "admin@elvaragroup.com";

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/campaigns", icon: Megaphone, label: "Campaigns" },
    { path: "/advisor", icon: Sparkles, label: "Advisor" },
    { path: "/simulator", icon: FlaskConical, label: "AI Simulator" },
    { path: "/optimization", icon: TrendingUp, label: "Optimization" },
    { path: "/settings", icon: Settings, label: "Settings" },
    ...(isAdmin ? [{ path: "/admin/users", icon: Users, label: "Admin" }] : []),
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={onClose}
        />
      )}
      
      <aside 
        className={cn(
          "fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col z-40 transition-transform duration-300",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Header */}
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center">
              <img src="/roas-ai-logo.png" alt="ROAS-AI" className="w-9 h-9 object-contain" />
            </div>
            <span className="font-heading text-lg text-white font-bold">ROAS-AI</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-zinc-400 hover:text-white"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6" data-testid="main-navigation">
          <div className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  onClick={() => {
                    navigate(item.path);
                    onClose?.();
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200",
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Standalone Product Simulator (separate /sim product) */}
          <div className="mt-6 pt-4 border-t border-white/5">
            <button
              data-testid="nav-product-simulator"
              onClick={() => { window.open("/sim", "_blank", "noopener"); onClose?.(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-cyan-300/90 hover:text-cyan-200 hover:bg-cyan-500/5 transition-colors"
            >
              <Radar className="w-5 h-5" />
              <span className="flex-1 text-left">Product Simulator</span>
              <ArrowUpRight className="w-4 h-4 opacity-70" />
            </button>
          </div>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar className="w-9 h-9">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-white/10 text-white text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            onClick={handleLogout}
            className="w-full mt-3 justify-start text-zinc-400 hover:text-white hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Log out
          </Button>
        </div>
      </aside>
    </>
  );
};
